/**
 * New Photo List page (/photos/daily/new-run) — the dedicated upload
 * surface, mirroring the audit's New Audit Run page: the Daily Photo List
 * page shows the list, this page updates it. Holds the File Requirements
 * panel (drop zone + per-vendor staging), the Active Vendor Files panel, and
 * the run button. A finished run drops you back on the Daily Photo List,
 * which always opens on the group's latest run.
 *
 * The content component is keyed by dealer group: switching groups remounts
 * it, so in-flight uploads from the old group can never paint their results
 * over the new group's state.
 */
import React, { useCallback, useState } from "react"
import { Link, useNavigate } from "react-router-dom"

import Layout from "../../components/Layout"
import ActiveVendorFilesPanel from "../../components/photos/ActiveVendorFilesPanel"
import DealerGroupRequiredNotice from "../../components/DealerGroupRequiredNotice"
import FileRequirementsPanel, { UploadingFile } from "../../components/photos/FileRequirementsPanel"
import { RunPhotoListButton } from "../../components/photos/RunPhotoListButton"
import { useDealerGroup } from "../../context/DealerGroupContext"
import { PhotoListProvider, usePhotoList } from "../../context/PhotoListContext"
import { THEME } from "../../config/theme"
import { useIsMobile } from "../../hooks/useIsMobile"
import { detectVendorFromFilename } from "../../utils/vendorDetection"

export default function PhotoListNewRunPage() {
  return (
    <Layout>
      <PhotoListProvider>
        <PhotoListNewRunPageBody />
      </PhotoListProvider>
    </Layout>
  )
}

function PhotoListNewRunPageBody() {
  const { dealerGroupId } = useDealerGroup()
  const isMobile = useIsMobile()

  if (dealerGroupId === null) {
    return (
      <div style={{ padding: isMobile ? THEME.spacing.md : THEME.spacing.lg }}>
        <DealerGroupRequiredNotice surface="photo list uploads" />
      </div>
    )
  }

  return <PhotoListNewRunContent key={dealerGroupId} isMobile={isMobile} />
}

function PhotoListNewRunContent({ isMobile }: { isMobile: boolean }) {
  const navigate = useNavigate()
  const {
    fileStatus,
    fileStatusLoading,
    refreshFileStatus,
    isRunning,
    lastRunCompletedAt,
  } = usePhotoList()

  // Upload tracking — keyed by original filename, maps to requirement row.
  const [uploadingFiles, setUploadingFiles] = useState<Map<string, UploadingFile>>(
    () => new Map(),
  )

  const handleUploadStart = useCallback((files: File[]) => {
    setUploadingFiles((prev) => {
      const next = new Map(prev)
      for (const f of files) {
        next.set(f.name, {
          vendor: detectVendorFromFilename(f.name),
          status: "uploading",
        })
      }
      return next
    })
  }, [])

  const handleUploadComplete = useCallback(
    (filename: string) => {
      setUploadingFiles((prev) => {
        const next = new Map(prev)
        const entry = next.get(filename)
        if (entry) next.set(filename, { ...entry, status: "complete" })
        return next
      })
      refreshFileStatus()
      // Remove completed entry after 2s
      setTimeout(() => {
        setUploadingFiles((prev) => {
          const next = new Map(prev)
          next.delete(filename)
          return next
        })
      }, 2000)
    },
    [refreshFileStatus],
  )

  const handleUploadError = useCallback(
    (filename: string, error: string) => {
      setUploadingFiles((prev) => {
        const next = new Map(prev)
        const entry = next.get(filename)
        if (entry) next.set(filename, { ...entry, status: "error", error })
        return next
      })
    },
    [],
  )

  // The panel uploads a dropped batch sequentially after collapsing itself, so
  // the Generate button must stay locked until every file in the batch has
  // landed — not just the required ones the can_run gate counts. Errored
  // entries don't hold the lock; the operator can still run without an
  // optional vendor, they just do it knowingly.
  const uploadsInFlight = Array.from(uploadingFiles.values()).some(
    (f) => f.status === "uploading",
  )

  return (
    <div
      style={{
        padding: isMobile ? THEME.spacing.md : THEME.spacing.lg,
        maxWidth: 1160,
        margin: "0 auto",
      }}
    >
      <Link
        to="/photos/daily"
        style={{
          fontSize: 12.5,
          color: THEME.colors.neutral.light.textSecondary,
          textDecoration: "none",
        }}
      >
        ← Daily Photo List
      </Link>
      <h1
        style={{
          fontSize: isMobile ? 20 : 24,
          fontWeight: 500,
          letterSpacing: "-0.5px",
          margin: `4px 0 ${THEME.spacing.md}`,
          color: THEME.colors.neutral.light.textPrimary,
        }}
      >
        New Photo List
      </h1>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: THEME.spacing.sm,
          marginBottom: THEME.spacing.md,
        }}
      >
        <FileRequirementsPanel
          fileStatus={fileStatus}
          loading={fileStatusLoading}
          lastRunCompletedAt={lastRunCompletedAt}
          uploadingFiles={uploadingFiles}
          onUploadStart={handleUploadStart}
          onUploadComplete={handleUploadComplete}
          onUploadError={handleUploadError}
          uploadDisabled={isRunning}
        />

        <ActiveVendorFilesPanel
          fileStatus={fileStatus}
          onFileDeleted={refreshFileStatus}
        />
      </div>

      {/* The flow's terminal action anchors bottom-right (platform grammar:
          title left, actions right), matching the New Audit Run page. On
          mobile the button is full-width, so the alignment is moot. */}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <RunPhotoListButton
          fullWidth={isMobile}
          uploadsInFlight={uploadsInFlight}
          // Carry the confirmation to the list page — navigating away here
          // unmounts the button before its own success message can render.
          onRunComplete={(run) =>
            navigate("/photos/daily", { state: { generatedTotal: run.total_count } })
          }
        />
      </div>
    </div>
  )
}
